import { backendUrl } from '@/config/backend';
import {
  getSchoolIdFromToken,
  getStoredSchoolId,
  getStoredToken,
  setStoredSchoolId,
  syncActiveSchool,
} from '@/config/schools';

const TOKEN_STORAGE_KEY = 'token';
const TOKEN_QUERY_PARAM = 'token';
const LOGIN_REDIRECT_QUERY_PARAM = 'loginRedirect';

function getWindowLocationHref() {
  return typeof window === 'undefined' ? null : window.location.href;
}

function normalizeOriginalUrl(originalUrl = null, fallbackPath = '/dashboard') {
  if (typeof window === 'undefined') {
    return originalUrl || fallbackPath;
  }

  const fallbackUrl = new URL(fallbackPath, window.location.origin).toString();
  if (!originalUrl) {
    return getWindowLocationHref() || fallbackUrl;
  }

  try {
    return new URL(originalUrl, window.location.origin).toString();
  } catch (error) {
    console.warn('Unable to normalize login redirect URL:', error);
    return fallbackUrl;
  }
}

export function resolveLoginRedirectTarget(redirectValue = null, fallbackPath = '/dashboard') {
  return normalizeOriginalUrl(redirectValue, fallbackPath);
}

export function storeAuthToken(token) {
  if (typeof window === 'undefined' || !token) {
    return null;
  }

  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (error) {
    console.warn('Unable to store auth token in localStorage:', error);
  }

  const tokenSchoolId = getSchoolIdFromToken(token);
  if (tokenSchoolId) {
    setStoredSchoolId(tokenSchoolId);
  }

  syncActiveSchool({ token });
  return token;
}

export function clearAuthToken({ preserveSchool = true } = {}) {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch (error) {
      console.warn('Unable to clear auth token from localStorage:', error);
    }
  }

  const schoolId = preserveSchool ? getStoredSchoolId() : null;
  syncActiveSchool({ schoolId, persist: preserveSchool });
}

export function buildLoginUrl({ originalUrl = null, schoolId = null, mobile = false } = {}) {
  const resolvedSchoolId = schoolId || getStoredSchoolId();
  if (!resolvedSchoolId) {
    return null;
  }

  const params = new URLSearchParams({
    originalUrl: normalizeOriginalUrl(originalUrl),
    schoolId: resolvedSchoolId,
  });

  if (mobile) {
    params.set('mobile', 'true');
  }

  return backendUrl(`/login?${params.toString()}`);
}

export function buildSchoolPickerUrl({ originalUrl = null } = {}) {
  if (typeof window === 'undefined') {
    return '/';
  }

  const pickerUrl = new URL('/', window.location.origin);
  pickerUrl.searchParams.set(
    LOGIN_REDIRECT_QUERY_PARAM,
    normalizeOriginalUrl(originalUrl),
  );
  return pickerUrl.toString();
}

export function redirectToLogin({ originalUrl = null, schoolId = null, mobile = false } = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  const loginUrl = buildLoginUrl({ originalUrl, schoolId, mobile });
  window.location.href = loginUrl || buildSchoolPickerUrl({ originalUrl });
}

export function consumeTokenFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  const currentUrl = new URL(window.location.href);
  const token = currentUrl.searchParams.get(TOKEN_QUERY_PARAM);
  if (!token) {
    return null;
  }

  storeAuthToken(token);
  currentUrl.searchParams.delete(TOKEN_QUERY_PARAM);

  const replacementUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  window.history.replaceState({}, document.title, replacementUrl);

  return token;
}

export function getActiveAuthToken() {
  return getStoredToken();
}
